import { useState } from 'react'
import { FileText } from 'lucide-react'
import { Alert, Badge, Button, CopyButton, DetailList, Drawer, EmptyState, Skeleton } from '@ideon-api/ideon-ui'
import { CarrierName } from '../../components/CarrierName'
import { bandCounts, dataQualityFlags, displayCodes, groupEntries, neverGranted, type DataQualityFlag } from '../../lib/privileged-access'
import { lineLabel } from '../../lib/product-lines'
import { FLAG_BADGE } from '../../lib/privileged-labels'
import type { AuthPrivilegedAccessEntry } from '@ideon-api/sdk-internal'
import { usePrivilegedAccess } from '../../lib/use-privileged-access'
import { Codes, countLabel } from './CoverageLine'
import { Panel } from '../../components/Panel'

function Band({ variant, label, count }: { variant: 'success' | 'error'; label: string; count: number }) {
  return (
    <div className="flex items-center gap-3">
      <Badge variant={variant} dot={false}>
        {label}
      </Badge>
      <span className="text-[13px] text-ideon-gray-70">{count}</span>
    </div>
  )
}

// DetailList truncates its value cell; whitespace-normal lets a long state list wrap instead.
const GRANTED_TEXT = 'whitespace-normal text-ideon-gray-black'
const DENIED_TEXT = 'whitespace-normal text-ideon-gray-70'

// Null means the row does not exist (hidden); [] means it exists and is empty (shows "none").
function CarrierStates({ entry }: { entry: AuthPrivilegedAccessEntry }) {
  const { granted, noAccess } = bandCounts(entry)
  const id = `privileged-${entry.productLine}-${entry.carrier}`
  return (
    <div className="flex flex-col gap-3">
      {entry.effectiveStates.length > 0 && (
        <div className="flex justify-end">
          <CopyButton value={entry.effectiveStates.join(', ')} label="Copy states" />
        </div>
      )}
      <Band variant="success" label="Granted" count={granted} />
      <DetailList.Root>
        {entry.wildcardStates && (
          <DetailList.Item
            label="Wildcard"
            value={<Codes codes={displayCodes(entry.wildcardStates)} className={GRANTED_TEXT} />}
            data-testid={`${id}-wildcard`}
          />
        )}
        {entry.grantedStates && (
          <DetailList.Item
            label="Specifically granted"
            value={<Codes codes={displayCodes(entry.grantedStates)} className={GRANTED_TEXT} />}
            data-testid={`${id}-granted`}
          />
        )}
      </DetailList.Root>
      <Band variant="error" label="No access" count={noAccess} />
      <DetailList.Root>
        <DetailList.Item
          label="Specifically negated"
          value={<Codes codes={displayCodes(entry.negatedStates)} className={DENIED_TEXT} />}
          data-testid={`${id}-negated`}
        />
        <DetailList.Item
          label="Never granted"
          value={<Codes codes={neverGranted(entry)} className={DENIED_TEXT} />}
          data-testid={`${id}-never`}
        />
      </DetailList.Root>
    </div>
  )
}

function CarrierRow({ entry, flags, onOpen }: { entry: AuthPrivilegedAccessEntry; flags: DataQualityFlag[]; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 border-t border-ideon-gray-40 px-4 py-3 text-left first:border-t-0 hover:bg-ideon-gray-10"
    >
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold text-ideon-gray-black">
          <CarrierName slug={entry.carrier} raw={flags.includes('case_variant_duplicate')} />
        </span>
        <span className="text-xs text-ideon-gray-70">{lineLabel(entry.productLine)}</span>
        {flags.map((f) => (
          <Badge key={f} variant={FLAG_BADGE[f].variant} dot={false}>
            {FLAG_BADGE[f].label}
          </Badge>
        ))}
      </span>
      <span className="ml-auto text-[13px] text-ideon-gray-70">{countLabel(entry.effectiveStates)}</span>
    </button>
  )
}

function CarrierDrawer({
  entry,
  flags,
  isOpen,
  onClose,
}: {
  entry: AuthPrivilegedAccessEntry
  flags: DataQualityFlag[]
  isOpen: boolean
  onClose: () => void
}) {
  return (
    <Drawer.Root isOpen={isOpen} onClose={onClose} size="lg" testIdPrefix="carrier-drawer">
      <Drawer.Header className="mb-4 border-b border-ideon-gray-40 pb-4">
        <div className="flex min-w-0 flex-col gap-1">
          <Drawer.Title className="text-lg leading-7">
            <CarrierName slug={entry.carrier} raw={flags.includes('case_variant_duplicate')} />
          </Drawer.Title>
          <span className="text-sm text-ideon-gray-70">{lineLabel(entry.productLine)}</span>
        </div>
        <Drawer.CloseButton />
      </Drawer.Header>
      <Drawer.Body>
        <CarrierStates entry={entry} />
      </Drawer.Body>
    </Drawer.Root>
  )
}

// The fixed groups render their cards at once, so the page does not shift when data lands.
export function PrivilegedSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      {groupEntries([]).map((group) => (
        <Panel key={group.key} title={group.title} >
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 border-t border-ideon-gray-40 px-4 py-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="ml-auto h-4 w-16" />
            </div>
          ))}
        </Panel>
      ))}
    </div>
  )
}

// Keyed by permission set, so the open drawer never carries another set's entry over.
export const PrivilegedTab = ({ permissionSetKey }: { permissionSetKey: string }) => (
  <PrivilegedSetView key={permissionSetKey} permissionSetKey={permissionSetKey} />
)

function PrivilegedSetView({ permissionSetKey }: { permissionSetKey: string }) {
  const state = usePrivilegedAccess(permissionSetKey)
  // The entry stays set after close so the drawer can run its close transition.
  const [openEntry, setOpenEntry] = useState<AuthPrivilegedAccessEntry | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  if (state.status === 'error') {
    return (
      <Alert variant="error" action={<Button onClick={state.reload}>Retry</Button>}>
        Failed to load: {state.message}
      </Alert>
    )
  }
  if (state.status !== 'ready') {
    return <PrivilegedSkeleton />
  }

  const flags = dataQualityFlags(state.data)
  return (
    <div className="flex flex-col gap-6">
      {groupEntries(state.data).map((group) => (
        <Panel
          key={group.key}
          title={group.title}
          subtitle={group.entries.length ? `${group.entries.length} ${group.entries.length === 1 ? 'carrier' : 'carriers'}` : undefined}
        >
          {group.entries.length === 0 ? (
            <EmptyState
              className="m-4"
              icon={<FileText className="h-10 w-10" />}
              heading="Nothing granted"
              description={`No ${group.noun} carrier grants for this permission set.`}
            />
          ) : (
            group.entries.map((entry) => (
              <CarrierRow
                key={`${entry.productLine}:${entry.carrier}`}
                entry={entry}
                flags={flags.get(entry) ?? []}
                onOpen={() => {
                  setOpenEntry(entry)
                  setDrawerOpen(true)
                }}
              />
            ))
          )}
        </Panel>
      ))}
      {openEntry && <CarrierDrawer entry={openEntry} flags={flags.get(openEntry) ?? []} isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />}
    </div>
  )
}
